/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { CubismFramework, Option } from '@framework/live2dcubismframework';
import * as LAppDefine from './lappdefine';
import { LAppPal } from './lapppal';
import { LAppSubdelegate } from './lappsubdelegate';
import { CubismLogError } from '@framework/utils/cubismdebug';

export let s_instance: LAppDelegate = null;

/**
 * 应用程序类。
 * 负责管理Cubism SDK。
 */
export class LAppDelegate {
  /**
   * 返回类的实例（单例）。
   * 如果实例尚未生成，则在内部生成实例。
   *
   * @return 类的实例
   */
  public static getInstance(): LAppDelegate {
    if (s_instance == null) {
      s_instance = new LAppDelegate();
    }

    return s_instance;
  }

  /**
   * 释放类的实例（单例）。
   */
  public static releaseInstance(): void {
    if (s_instance != null) {
      s_instance.release();
    }

    s_instance = null;
  }

  /**
   * 当指针变为活动状态时被调用。
   */
  private onPointerBegan(e: PointerEvent): void {
    for (let i = 0; i < this._subdelegates.length; i++) {
      this._subdelegates[i].onPointBegan(e.clientX, e.clientY);
    }
  }

  /**
   * 当指针移动时被调用。
   */
  private onPointerMoved(e: PointerEvent): void {
    this._lastMoveTime = Date.now();
    for (let i = 0; i < this._subdelegates.length; i++) {
      this._subdelegates[i].onPointMoved(e.clientX, e.clientY);
    }
  }

  /**
   * 当指针不再活动时被调用。
   */
  private onPointerEnded(e: PointerEvent): void {
    for (let i = 0; i < this._subdelegates.length; i++) {
      this._subdelegates[i].onPointEnded(e.clientX, e.clientY);
    }
  }

  /**
   * 当指针被取消时被调用。
   */
  private onPointerCancel(e: PointerEvent): void {
    for (let i = 0; i < this._subdelegates.length; i++) {
      this._subdelegates[i].onTouchCancel(e.clientX, e.clientY);
    }
  }

  /**
   * 记录最后一次鼠标位置和时间（用于空闲检测）。
   */
  private _lastMoveTime = 0;
  private _idleCheckTimer: ReturnType<typeof setInterval> | null = null;
  private readonly IDLE_TIMEOUT_MS = 30000; // 30秒

  /**
   * 当鼠标离开页面时，重置看板娘视角为正视。
   */
  private onPointerLeave(_e?: Event): void {
    console.log('[Live2D] mouse left browser');
    this._lastMoveTime = 0;
    this.resetLookToCenter();
  }

  /**
   * 当页面不可见时（切标签页等），重置看板娘视角。
   */
  private onVisibilityChange(): void {
    if (document.hidden) {
      console.log('[Live2D] page hidden');
      this._lastMoveTime = 0;
      this.resetLookToCenter();
    }
  }

  /** 启动空闲检测定时器 */
  private startIdleTimer(): void {
    if (this._idleCheckTimer != null) return;
    this._idleCheckTimer = setInterval(() => {
      if (this._lastMoveTime === 0) return; // 已正视
      if (Date.now() - this._lastMoveTime > this.IDLE_TIMEOUT_MS) {
        console.log(`[Live2D] idle ${this.IDLE_TIMEOUT_MS/1000}s, resetting`);
        this._lastMoveTime = 0;
        this.resetLookToCenter();
      }
    }, 1000); // 每秒检查一次
  }

  /**
   * 重置看板娘视角到正中（正视）—— 直接设参数，不依赖平滑插值。
   */
  public resetLookToCenter(): void {
    console.log('[Live2D] resetLookToCenter');
    for (let i = 0; i < this._subdelegates.length; i++) {
      const manager = this._subdelegates[i].getLive2DManager();
      if (manager) {
        manager.onDrag(0.0, 0.0);
        // 直接重置模型参数，确保即时正视
        const model = manager._models[0];
        if (model && model._model) {
          const eyeBallX = model.getId('ParamEyeBallX');
          const eyeBallY = model.getId('ParamEyeBallY');
          const ids = [
            model._idParamAngleX, model._idParamAngleY,
            model._idParamAngleZ, model._idParamBodyAngleX,
            eyeBallX, eyeBallY
          ];
          for (const id of ids) {
            const idx = model._model.getParameterIndex(id);
            if (idx >= 0) model._model.setParameterValueByIndex(idx, 0.0);
          }
          model._model.saveParameters();
          console.log('[Live2D] set head + eyes to 0');
        }
      }
    }
  }

  /**
   * 调整画布大小并重新初始化视图。
   */
  public onResize(): void {
    for (let i = 0; i < this._subdelegates.length; i++) {
      this._subdelegates[i].onResize();
    }
  }

  /**
   * 执行处理。
   */
  public run(): void {
    // 主循环
    const loop = (): void => {
      // 确认实例是否存在
      if (s_instance == null) {
        return;
      }

      // 更新时间
      LAppPal.updateTime();

      for (let i = 0; i < this._subdelegates.length; i++) {
        this._subdelegates[i].update();
      }

      // 通过递归调用实现循环
      requestAnimationFrame(loop);
    };
    loop();
  }

  /**
   * 释放资源。
   */
  private release(): void {
    this.releaseEventListener();
    this.releaseSubdelegates();

    // 释放Cubism SDK
    CubismFramework.dispose();

    this._cubismOption = null;
  }

  /**
   * 解除事件监听器。
   */
  private releaseEventListener(): void {
    document.removeEventListener('pointerup', this.pointBeganEventListener);
    this.pointBeganEventListener = null;
    document.removeEventListener('pointermove', this.pointMovedEventListener);
    this.pointMovedEventListener = null;
    document.removeEventListener('pointerdown', this.pointEndedEventListener);
    this.pointEndedEventListener = null;
    document.removeEventListener('pointerdown', this.pointCancelEventListener);
    this.pointCancelEventListener = null;
    document.documentElement.removeEventListener('mouseleave', this.pointLeaveEventListener);
    document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    this.pointLeaveEventListener = null;
    this.visibilityChangeListener = null;
    if (this._idleCheckTimer != null) {
      clearInterval(this._idleCheckTimer);
      this._idleCheckTimer = null;
    }
  }

  /**
   * 释放Subdelegate
   */
  private releaseSubdelegates(): void {
    for (let i = 0; i < this._subdelegates.length; i++) {
      this._subdelegates[i].release();
    }

    this._subdelegates.length = 0;
    this._subdelegates = null;
  }

  /**
   * 初始化应用所需的内容。
   */
  public initialize(): boolean {
    // 初始化Cubism SDK
    this.initializeCubism();

    this.initializeSubdelegates();
    this.initializeEventListener();

    return true;
  }

  /**
   * 设置事件监听器。
   */
  private initializeEventListener(): void {
    this.pointBeganEventListener = this.onPointerBegan.bind(this);
    this.pointMovedEventListener = this.onPointerMoved.bind(this);
    this.pointEndedEventListener = this.onPointerEnded.bind(this);
    this.pointCancelEventListener = this.onPointerCancel.bind(this);
    this.pointLeaveEventListener = this.onPointerLeave.bind(this);
    this.visibilityChangeListener = this.onVisibilityChange.bind(this);

    // 注册指针相关的回调函数
    document.addEventListener('pointerdown', this.pointBeganEventListener, {
      passive: true
    });
    document.addEventListener('pointermove', this.pointMovedEventListener, {
      passive: true
    });
    document.addEventListener('pointerup', this.pointEndedEventListener, {
      passive: true
    });
    document.addEventListener('pointercancel', this.pointCancelEventListener, {
      passive: true
    });
    // 鼠标离开页面 / 页面不可见 → 正视
    document.documentElement.addEventListener('mouseleave', this.pointLeaveEventListener);
    document.addEventListener('visibilitychange', this.visibilityChangeListener);

    // 启动空闲检测定时器
    this.startIdleTimer();
  }

  /**
   * 初始化Cubism SDK
   */
  private initializeCubism(): void {
    LAppPal.updateTime();

    // 设置cubism
    this._cubismOption.logFunction = LAppPal.printMessage;
    this._cubismOption.loggingLevel = LAppDefine.CubismLoggingLevel;
    CubismFramework.startUp(this._cubismOption);

    // 初始化cubism
    CubismFramework.initialize();
  }

  /**
   * 生成配置Canvas，初始化Subdelegate
   */
  private initializeSubdelegates(): void {
    let width: number = 100;
    let height: number = 100;
    if (LAppDefine.CanvasNum > 3) {
      const widthunit: number = Math.ceil(Math.sqrt(LAppDefine.CanvasNum));
      const heightUnit = Math.ceil(LAppDefine.CanvasNum / widthunit);
      width = 100.0 / widthunit;
      height = 100.0 / heightUnit;
    } else {
      width = 100.0 / LAppDefine.CanvasNum;
    }

    this._canvases.length = LAppDefine.CanvasNum;
    this._subdelegates.length = LAppDefine.CanvasNum;
    for (let i = 0; i < LAppDefine.CanvasNum; i++) {
      const canvas = document.createElement('canvas');
      canvas.id = 'live2d-canvas';
      this._canvases[i] = canvas;
      // 看板娘：左下角固定，240×270
      canvas.style.position = 'fixed';
      canvas.style.left = '0';
      canvas.style.bottom = '0';
      canvas.style.width = '240px';
      canvas.style.height = '270px';

      // 将画布添加到DOM中
      document.body.appendChild(canvas);
    }

    for (let i = 0; i < this._canvases.length; i++) {
      const subdelegate = new LAppSubdelegate();
      subdelegate.initialize(this._canvases[i]);
      this._subdelegates[i] = subdelegate;
    }

    for (let i = 0; i < LAppDefine.CanvasNum; i++) {
      if (this._subdelegates[i].isContextLost()) {
        CubismLogError(
          `The context for Canvas at index ${i} was lost, possibly because the acquisition limit for WebGLRenderingContext was reached.`
        );
      }
    }
  }

  /**
   * 私有构造函数
   */
  private constructor() {
    this._cubismOption = new Option();
    this._subdelegates = new Array<LAppSubdelegate>();
    this._canvases = new Array<HTMLCanvasElement>();
  }

  /**
   * Cubism SDK选项
   */
  private _cubismOption: Option;

  /**
   * 操作目标的canvas元素
   */
  private _canvases: Array<HTMLCanvasElement>;

  /**
   * Subdelegate
   */
  private _subdelegates: Array<LAppSubdelegate>;

  /**
   * 已注册的事件监听器函数对象
   */
  private pointBeganEventListener: (this: Document, ev: PointerEvent) => void;

  /**
   * 已注册的事件监听器函数对象
   */
  private pointMovedEventListener: (this: Document, ev: PointerEvent) => void;

  /**
   * 已注册的事件监听器函数对象
   */
  private pointEndedEventListener: (this: Document, ev: PointerEvent) => void;

  /**
   * 已注册的事件监听器函数对象
   */
  private pointCancelEventListener: (this: Document, ev: PointerEvent) => void;

  /**
   * 指针离开事件监听器
   */
  private pointLeaveEventListener: (this: Document, ev: MouseEvent) => void;

  /**
   * 页面可见性变化监听器
   */
  private visibilityChangeListener: () => void;
}
